/**
 * The shared component vocabulary.
 *
 * Every value here is the OZORA pilot's own — its radii, its type sizes, its
 * act-row density — because people who used the pilot should recognise this
 * app. Where a component is new (the pilot had no design system, only a
 * 190KB HTML file), it is built from the pilot's equivalent markup rather than
 * invented: `ActRow` is its `.act`, `StageSection` its `.stage-sec`, `DayPill`
 * its `.pill`, `TrackTab` its `.ttab`.
 *
 * Three rules run through all of it:
 *  - Colour arrives as a PROP from edition data, never as a constant. A
 *    festival with a different palette needs no change here.
 *  - Every hit target is at least `--tap` (44px). This is used one-handed,
 *    in a field, at 04:00.
 *  - The styling is CLASSES in `components.css`, not an inline object per
 *    render. Inline `style` is left for what varies per call — a stage's
 *    colour, a size, a caller's override — and a caller's `style` still wins.
 *
 * The components' own words ("Close", "Try again") go through `useUiT()`, the
 * translator the app hands down (`./text`); callers' text props are theirs.
 */
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { useUiT } from './text';
import { TypedLine } from './TypedLine';
import { lockScroll } from './Sheet';

export { UiTextContext, useUiT, type UiTranslate } from './text';
export { LoadingLinesProvider, TypedLine, type LoadingLinePool } from './TypedLine';

/** Class names, skipping the falsy ones. */
export function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

/**
 * The ground everything else assumes.
 *
 * In the web app this comes from `body` in tokens.css, so nothing needs to
 * wrap anything. Everywhere else it does not: a React Native screen, a
 * preview card, a design tool composing these components into a mockup. Those
 * hosts paint their own background — usually white — and every text colour
 * here is near-white, so the result is invisible text.
 *
 * Same three radial washes as the pilot, at the pilot's values (`--atmosphere`).
 */
export function Surface({ children, style, className, padded = true }: { children: ReactNode; style?: CSSProperties; className?: string; padded?: boolean }) {
  return <div className={cx('ui-surface', !padded && 'is-flush', className)} style={style}>{children}</div>;
}

/**
 * The measure every screen is read at.
 *
 * The pilot caps its content at `--maxw` (980px) and centres it — `.wrap` and
 * `main` both do it — so a desktop browser shows a readable column rather than
 * an act row stretched across 2560px with the time at one edge and the heart
 * at the other. MyRave had the token and used it nowhere, which is why the
 * schedule filled the whole screen.
 *
 * `full` keeps the element itself edge to edge — a sticky bar or a nav needs
 * its background and border to span the viewport while its CONTENTS stay on
 * the measure.
 */
export function Wrap({
  children, style, className, full = false,
}: { children: ReactNode; style?: CSSProperties; className?: string; full?: boolean }) {
  const inner = <div className={cx('ui-wrap', className)} style={style}>{children}</div>;
  return full ? <div className="ui-full">{inner}</div> : inner;
}

/*
 * ---------------------------------------------------------------------------
 * The brand: three assets, deliberately separable.
 * ---------------------------------------------------------------------------
 *
 * `LogoMark` alone, `Wordmark` alone, and `BrandLockup` combining them. They
 * are separate exports rather than one component with a `showText` flag
 * because they are used in places that have nothing to do with each other —
 * the mark is an app icon and a 16px favicon, the wordmark is a page title and
 * a splash screen, and only the lockup is the header.
 *
 * Every colour is the pilot's, as `--brand*` tokens — never `--accent`, which
 * is the festival's and may be repainted by an edition.
 */

/** Where the mark is being drawn, which decides its ink rather than its shape. */
export type BrandTone = 'dark' | 'light';

/**
 * The mark. Five levelmeter bars that also read as an M, in a radar orbit,
 * with a beacon on the tallest peak.
 *
 * Under 40px the orbit and the beacon stop being detail and become dirt, so
 * `size` picks the simplified silhouette on its own rather than leaving it to
 * each call site to remember. That threshold was set by looking at the render.
 *
 * The fills are classes (`.ui-mark .m-o` and friends), not presentation
 * attributes: an attribute cannot name a token, and a class can.
 */
export function LogoMark({
  size = 28, tone = 'dark', title, spin = false,
}: {
  size?: number; tone?: BrandTone; title?: string;
  /**
   * Turn the orbit ring and the beacon that sits on it.
   *
   * They are two groups, not one, because the ring is drawn BEHIND the bars
   * and the beacon in front of them — folding them together would drag the
   * dashed ring over the artwork. They mount in the same frame with the same
   * animation, so they stay in phase.
   *
   * Only the loading screen asks for this. A mark that spins wherever it
   * appears is a logo nobody can read.
   */
  spin?: boolean;
}) {
  const simple = size < 40;
  const id = `mr${tone}${simple ? 's' : 'f'}`;
  return (
    <svg
      width={size} height={size} viewBox="0 0 160 160" fill="none"
      role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}
      className="ui-mark" data-tone={tone}
    >
      {!simple && (
        <>
          <defs>
            <linearGradient id={`${id}w`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" className="s-p" />
              <stop offset="50%" className="s-o" />
              <stop offset="100%" className="s-a" />
            </linearGradient>
            <linearGradient id={`${id}r`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" className="s-o" />
              <stop offset="100%" className="s-a" />
            </linearGradient>
          </defs>
          <g className={spin ? 'brand-orbit' : undefined}>
            <circle cx="80" cy="80" r="54" stroke={`url(#${id}r)`} strokeWidth="1.2" strokeDasharray="5 7" opacity=".35" />
          </g>
          <circle cx="80" cy="80" r="44" className="m-ring" strokeWidth="1" />
          <circle cx="80" cy="80" r="32" className="m-o" opacity={tone === 'light' ? '.08' : '.12'} />
        </>
      )}

      {simple ? (
        /* `m-bars`: the meter's beats, there for whoever switches them on —
           the menu button does, under a pointer (`.shell-toggle:hover`). */
        <g className={spin ? 'm-bars brand-meter' : 'm-bars'}>
          <rect x="19" y="79" width="18" height="46" rx="9" className="m-o" opacity=".85" />
          <rect x="45" y="35" width="18" height="90" rx="9" className="m-o" />
          <rect x="71" y="63" width="18" height="62" rx="9" className="m-a" />
          <rect x="97" y="35" width="18" height="90" rx="9" className="m-o" />
          <rect x="123" y="79" width="18" height="46" rx="9" className="m-o" opacity=".85" />
        </g>
      ) : (
        <>
          {/* Spinning, the bars are a meter too (the user, 2026-10-07): each
              rises and falls on its own beat — `.brand-meter` in tokens.css. */}
          <g className={spin ? 'm-bars brand-meter' : 'm-bars'}>
            <rect x="40" y="72" width="10" height="34" rx="5" className="m-o" opacity=".85" />
            <rect x="56" y="46" width="10.5" height="60" rx="5.25" fill={`url(#${id}w)`} />
            <rect x="74" y="60" width="11" height="46" rx="5.5" className="m-a" />
            <rect x="92" y="40" width="10.5" height="66" rx="5.25" fill={`url(#${id}w)`} />
            <rect x="110" y="66" width="10" height="40" rx="5" className="m-o" opacity=".85" />
          </g>
          {/* The beacon sits exactly on the r=54 orbit — 53.8 from centre —
              so turning both about (80,80) reads as one dot going round. */}
          <g className={spin ? 'brand-orbit' : undefined}>
            <circle cx="97.25" cy="29" r="4" className="m-beacon" />
            <circle cx="97.25" cy="29" r="7.5" className="m-halo" strokeWidth="1.4" opacity=".8" />
          </g>
        </>
      )}
    </svg>
  );
}

/**
 * The wordmark: **MyRave.io**, exactly, never abbreviated — and never
 * translated: it is a name and a domain.
 *
 * Set in the app's own stack rather than drawn as paths, because there is no
 * webfont here and a wordmark in a font nobody has is a wordmark that renders
 * differently on every machine. system-ui is the pilot's, carries Hebrew, and
 * costs nothing to precache.
 *
 * `-webkit-text-fill-color` rather than `color: transparent` for the gradient:
 * where `background-clip: text` is not supported neither is the fill
 * property, so the text falls back to solid accent instead of to invisible.
 * An invisible wordmark is the exact failure `tokens.test.ts` exists for.
 *
 * `.io` is a bordered pill, not part of the word. The domain is the name, and
 * the pill is what stops "MyRave" and ".io" being read as one long word.
 *
 * `dir="ltr"`: in a Hebrew page the three pieces would otherwise be laid out
 * right to left and read "io. Rave My".
 */
export function Wordmark({ size = 19, tone = 'dark' }: { size?: number; tone?: BrandTone }) {
  return (
    /*
     * One accessible name for the whole mark.
     *
     * The three visual pieces — "My", the gradient "Rave", the `.io` pill —
     * are separate elements, so without this a screen reader announces the
     * product's name as three fragments and `getByText('MyRave')` cannot see
     * it at all. The test noticing that was the test noticing the real defect.
     */
    <span
      role="img"
      aria-label="MyRave.io"
      dir="ltr"
      className="brand-wordmark ui-wordmark"
      data-tone={tone}
      style={{ gap: Math.round(size * 0.42), fontSize: size }}
    >
      <span aria-hidden="true" className="wm-my">
        My
        <span className="wm-rave">Rave</span>
      </span>
      <span
        aria-hidden="true"
        className="wm-io"
        style={{
          fontSize: Math.max(9, Math.round(size * 0.55)),
          padding: `${Math.round(size * 0.1)}px ${Math.round(size * 0.3)}px`,
        }}
      >
        .io
      </span>
    </span>
  );
}

/**
 * Mark and wordmark together, at a fixed relationship.
 *
 * The gap is 0.29 of the mark's height in both orientations, so the lockup
 * holds its proportions at any size rather than needing a spacing rule
 * per context. `stacked` is for the places a horizontal lockup would have to
 * shrink below the wordmark's 15px floor — a splash screen, a narrow footer
 * column, a square social avatar.
 */
export function BrandLockup({
  size = 28, tone = 'dark', orientation = 'horizontal', href,
}: { size?: number; tone?: BrandTone; orientation?: 'horizontal' | 'stacked'; href?: string }) {
  const tr = useUiT();
  const stacked = orientation === 'stacked';
  /*
   * The mark is drawn 2.05x the wordmark when stacked, not 1.6x.
   *
   * Its bars occupy only the middle ~48% of a 160 viewBox — the rest is orbit
   * and breathing room — so matching the BOX heights leaves the artwork
   * looking undersized beside the caps. These two numbers were set by
   * rendering the lockup, not by arithmetic. The gap tightens for the same
   * reason: the mark brings its own padding with it.
   */
  const inner = (
    /* `dir="ltr"`: a logo is not mirrored in a right-to-left page. */
    <span
      dir="ltr"
      className={cx('ui-lockup', stacked && 'is-stacked')}
      style={{ gap: Math.round(size * (stacked ? 0.06 : 0.29)) }}
    >
      {/* The lockup announces once, from the wordmark. The mark beside it is
          decoration at that point — labelling both reads the name twice. */}
      <LogoMark size={stacked ? Math.round(size * 2.05) : size} tone={tone} />
      <Wordmark size={Math.round(size * (stacked ? 0.86 : 0.68))} tone={tone} />
    </span>
  );
  return href
    ? <a href={href} className="ui-lockup-link" aria-label={tr('MyRave.io home')}>{inner}</a>
    : inner;
}

/** A translucent tint of a hex colour — used for chips, blocks and tints. */
export function tint(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function Card({ children, style, className, onClick }: { children: ReactNode; style?: CSSProperties; className?: string; onClick?: () => void }) {
  return onClick
    ? <button onClick={onClick} className={cx('ui-card', className)} style={style}>{children}</button>
    : <div className={cx('ui-card', className)} style={style}>{children}</div>;
}

export function Button({
  children, onClick, variant = 'primary', full, disabled, type = 'button', loading, loadingLabel,
  autoFocus, form,
}: {
  children: ReactNode; onClick?: () => void;
  /*
   * `go` is the settled, already-yours action — "go to the thing you chose" —
   * as against `primary`, which is "choose it". A grid of cards then says
   * which is which before either label is read.
   *
   * `danger` is outlined rather than filled: a solid red block next to a
   * solid orange one reads as the emphasised choice, and the emphasised
   * choice in a confirm should never be the destructive one.
   */
  variant?: 'primary' | 'secondary' | 'quiet' | 'go' | 'danger'; full?: boolean; disabled?: boolean;
  type?: 'button' | 'submit';
  /** For a dialog that wants the CAUTIOUS button focused, not the other one. */
  autoFocus?: boolean;
  /*
   * A submit with no busy state is a submit people press twice. Every form
   * here was tracking its own `busy` flag and rendering its own wording, so
   * the same wait read as "Saving…", "Please wait" or nothing at all. The
   * button disables itself while loading — `disabled` alone would let a
   * second press through in the frame before state settled.
   */
  loading?: boolean; loadingLabel?: string;
  /** The id of the form this submits when it is drawn outside it (a sticky action bar). */
  form?: string;
}) {
  const tr = useUiT();
  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      disabled={disabled || loading}
      autoFocus={autoFocus}
      aria-busy={loading || undefined}
      className={cx('ui-btn', `ui-btn--${variant}`, full && 'is-full')}
    >
      {loading && <span aria-hidden="true" className="ui-busy" />}
      {loading ? (loadingLabel ?? tr('Working…')) : children}
    </button>
  );
}

/**
 * A round icon-only control — the pilot's `.modesw`. Always labelled, because
 * an icon is not a name.
 */
export function IconButton({
  children, label, onClick, active,
}: { children: ReactNode; label: string; onClick?: () => void; active?: boolean }) {
  return (
    <button onClick={onClick} aria-label={label} aria-pressed={active} className="ui-iconbtn">
      {children}
    </button>
  );
}

/** A stage chip. Its colour is the stage's own. */
export function Chip({
  label, color, active, onClick, style, className,
}: {
  label: string; color?: string; active?: boolean; onClick?: () => void;
  /** For a row that shares its width between the chips it actually has. */
  style?: CSSProperties; className?: string;
}) {
  // The label is elided rather than wrapped when the row is sharing its
  // width: a stage name on two lines makes one chip taller than the row.
  const tinted = active && color
    ? { background: tint(color, 0.18), borderColor: tint(color, 0.5), ...style }
    : style;
  return (
    <button onClick={onClick} className={cx('ui-chip', active && 'is-active', className)} style={tinted}>
      {color && <span className="ui-chip-dot" style={{ background: color }} />}
      <span className="ui-ellipsis">{label}</span>
    </button>
  );
}

export function Pill({ label, active, onClick }: { label: string; active?: boolean; onClick?: () => void }) {
  return (
    <button onClick={onClick} className={cx('ui-pill', active && 'is-active')}>
      {label}
    </button>
  );
}

/**
 * A day pill — weekday over date. The pilot's `.pill`, including its selected
 * state: a near-white gradient with dark ink, which is the one place the app
 * inverts. Two lines rather than one because "Fri 31" wraps badly at eleven
 * days across a 390px screen, and the date is what people scan for.
 */
export function DayPill({
  weekday, day, active, onClick, style, className, tag, passed, passedLabel, today, disabled,
}: {
  /** Today's day — marked when it is not the one chosen. */
  today?: boolean;
  weekday: string; day: string; active?: boolean; onClick?: () => void;
  /** For a row that shares its width between the days it actually has. */
  style?: CSSProperties; className?: string;
  /** Which day of the rave it is — "Day 5", "Day 0", "Day -1". */
  tag?: string;
  /** The day is over: drawn grey, and said so to a screen reader with `passedLabel`. */
  passed?: boolean; passedLabel?: string;
  /** Not pressable — the Weather page's days that are over (their cards are gone). */
  disabled?: boolean;
}) {
  return (
    <button onClick={onClick} disabled={disabled} aria-pressed={active} className={cx('ui-day', className)} style={style}
      data-passed={passed || undefined} data-today={today || undefined} aria-current={today ? 'date' : undefined}>
      <span className="ui-day-wd">{weekday}</span>
      <span className="ui-day-n">{day}</span>
      {tag && <span className="ui-day-tag">{tag}</span>}
      {passed && passedLabel && <span className="ui-sr-only">{passedLabel}</span>}
    </button>
  );
}

/** A track tab — music / body & mind / art & craft. The pilot's `.ttab`. */
export function TrackTab({
  label, active, onClick, icon,
}: { label: string; active?: boolean; onClick?: () => void; icon?: ReactNode }) {
  return (
    <button onClick={onClick} aria-pressed={active} className="ui-track">
      {icon}
      {label}
    </button>
  );
}

/**
 * A badge. `tone` never carries meaning on its own — every badge has a label,
 * because colour alone is not readable to everyone and not visible in
 * sunlight.
 *
 * `white-space: nowrap` because a pill that wraps is not a pill: "No booking
 * errors" in a squeezed flex row once broke onto three lines and the 999px
 * radius turned the badge into a circle with text spilling out of it.
 */
export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  return <span className={cx('ui-badge', tone !== 'neutral' && `ui-badge--${tone}`)}>{children}</span>;
}

/** A text input — the pilot's search field. Labelled by the caller. */
export function Field({
  value, onChange, placeholder, type = 'text', id, autoComplete,
}: {
  value?: string; onChange?: (v: string) => void; placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'search'; id?: string; autoComplete?: string;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      autoComplete={autoComplete}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      placeholder={placeholder}
      className="ui-field"
    />
  );
}

/**
 * A stage section — the header a day's acts hang under. The pilot's
 * `.stage-sec` with its `.nobanner` header.
 *
 * The stage's colour washes the header and nothing else: enough to tell
 * twenty-four stages apart while scrolling, not so much that the act names
 * fight it.
 */
export function StageSection({
  name, color, count, children,
}: { name: string; color: string; count?: number; children: ReactNode }) {
  return (
    <section className="ui-stage">
      <header className="ui-stage-hd" style={{ background: `linear-gradient(90deg, ${tint(color, 0.22)}, transparent)` }}>
        <div className="ui-stage-id">
          <span className="ui-stage-dot" style={{ background: color }} />
          <h2 className="ui-stage-name">{name}</h2>
        </div>
        {count !== undefined && <span className="ui-stage-count">{count}</span>}
      </header>
      <div className="ui-stage-body">{children}</div>
    </section>
  );
}

/**
 * The act row — the pilot's `.act`, and the densest thing in the product.
 *
 * Its shape was earned at a real festival: ~55px so a sixty-act stage day
 * scrolls without ceremony, times on the start edge because that is what
 * people scan, the stage's colour as a 3px edge.
 *
 * `color` is the STAGE's colour, passed in. There is no default.
 */
export function ActRow({
  start, end, name, sub, color, saved, live, clash, badge, highlight, divider,
  prefix, saveCount, sun, actions, leading, anchor, lit, onOpen, onToggleSave, more,
}: {
  start: string; end?: string; name: string; sub?: ReactNode; color: string;
  /**
   * The row's whole width, under it — a session's description with its own "Read more" (the 0.14.76 walk).
   * Outside the row's button, so it may hold a button of its own.
   */
  more?: ReactNode;
  saved?: boolean; live?: boolean; clash?: boolean;
  /**
   * Before the name, on the same line — a country flag, or the act's own
   * glyph. It belongs to the NAME, not to the sub-line: "🇮🇱 Modus" is how
   * the pilot writes it and how people read it, and a flag parked on a
   * separate row below reads as a second fact about a different thing.
   */
  prefix?: ReactNode;
  /** How many people have this saved. `undefined` until the count is known. */
  saveCount?: number;
  /**
   * Sunrise or sunset falling inside this set, on the second line under the
   * name. It was in the time column, where a two-line time plus a third row
   * pushed the 50px gutter out of shape on every row that had one.
   */
  sun?: ReactNode;
  /** Extra controls before the heart — play, show on map. */
  actions?: ReactNode;
  /**
   * Before the TIMES, on the inline start edge.
   *
   * For a list that mixes tracks — a plan holds a set and a yoga class and
   * they are not the same kind of thing. On the schedule, where a whole
   * screen is one track, there is nothing for it to say and it is left out.
   */
  leading?: ReactNode;
  /** NEW / UPDATED, beside the name. Always a LABEL — never colour alone. */
  badge?: ReactNode;
  /**
   * The pilot's warm wash for a set that runs through sunrise or sunset. It
   * is a tint and never the only signal: the sun time itself is in `sub`.
   */
  highlight?: boolean;
  /** The pilot's `.act + .act` hairline. Off on the last row of a section. */
  divider?: boolean;
  /**
   * `data-act-slug` — the stable anchor a search jump scrolls to
   * (`visitor/slotJump.ts`).
   */
  anchor?: string;
  /** Lit for a moment after a jump landed here: `data-highlight`. */
  lit?: boolean;
  onOpen?: () => void; onToggleSave?: () => void;
}) {
  const tr = useUiT();
  const countLabel = saveCount === undefined ? undefined
    : saveCount === 0 ? tr('Nobody has saved {name} yet', { name })
      : saveCount === 1 ? tr('1 person has {name} saved', { name })
        : tr('{count} people have {name} saved', { count: saveCount, name });
  return (
    <div
      data-print="act"
      data-act-slug={anchor}
      data-highlight={lit ? '' : undefined}
      className={cx('ui-act', !saved && highlight && 'is-sun', clash && 'is-clash', divider && 'has-divider')}
      style={{ borderInlineStartColor: color, background: saved ? tint(color, 0.12) : undefined }}
    >
      {leading && <div className="ui-act-lead">{leading}</div>}

      <div className="ui-act-time">
        {start}
        {end && <small className="ui-act-end">{end}</small>}
      </div>

      <button onClick={onOpen} className="ui-act-open">
        <div className="ui-act-line">
          {prefix && <span className="ui-act-prefix">{prefix}</span>}
          <span className="ui-act-name">{name}</span>
          {saveCount !== undefined && !onToggleSave && (
            /*
              * The crowd's answer, beside the name. Muted and small on
              * purpose: it is context for a decision, not a score — and at
              * 04:00 the thing that has to be legible is the name. A zero is
              * context, not news: it stays legible and stops competing with
              * the counts that mean something.
              */
            <span aria-label={countLabel} className={cx('ui-act-count', saveCount === 0 && 'is-zero')}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 21s-8-5.1-8-10.3A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8 3.7C20 15.9 12 21 12 21z" />
              </svg>
              {saveCount}
            </span>
          )}
          {badge}
        </div>
        {(sun || sub) && (
          <div className="ui-act-sub">
            {sun}
            {sub}
          </div>
        )}
      </button>

      {(onToggleSave || actions) && (
        <div className="ui-act-actions">
          {actions}
          {/* The crowd's count is INSIDE the heart, as on Live (2026-11-02); beside the name only where nobody saves here. */}
          {onToggleSave && <SaveHeart saved={!!saved} live={live} name={name} onToggle={onToggleSave} count={saveCount} />}
        </div>
      )}
      {more && <div className="ui-act-more">{more}</div>}
    </div>
  );
}

/**
 * THE save heart — the act row's, and every other place a set is saved from (Live, the board): one look and one
 * behaviour (the user, 2026-10-30: "the heart needs to be there, but OUR heart"). `compact` draws it smaller for a
 * board block; its tap area stays 44px (`::before`).
 *
 * With a `count` (the crowd's saves, the `crowd-stats` module) it is Live's pill — the heart and the number inside
 * one button, dark when not saved, red when saved — on every page that saves a set (the user, 2026-11-02: "hearts
 * with count inside same as live page in all pages that use hearts"). `null` / absent: the round heart alone.
 */
export function SaveHeart({ saved, name, onToggle, live = false, compact = false, className, count }: {
  saved: boolean; name: string; onToggle: () => void; live?: boolean; compact?: boolean; className?: string;
  count?: string | number | null;
}) {
  const tr = useUiT();
  const label = saved ? tr('Remove {name} from my lineup', { name }) : tr('Save {name} to my lineup', { name });
  const heart = (size: number) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 21s-8-5.1-8-10.3A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8 3.7C20 15.9 12 21 12 21z" />
    </svg>
  );
  if (count !== undefined && count !== null) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        aria-pressed={saved} aria-label={label}
        data-saved={saved ? '' : undefined}
        className={cx('ui-heart-pill', compact && 'is-compact', className)}
      >
        {heart(compact ? 13 : 16)}
        <span className="ui-heart-pill-count">{count}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      aria-pressed={saved}
      aria-label={label}
      className={cx('ui-heart', saved ? 'is-saved' : live && 'is-live', compact && 'is-compact', className)}
    >
      {heart(compact ? 13 : 16)}
    </button>
  );
}

/** A stage's colour dot, for use inside an act's sub-line. The pilot's `.sgdot`. */
export function StageDot({ color }: { color: string }) {
  return <span className="ui-stagedot" style={{ background: color }} />;
}

/**
 * `data-print` is passed through on the layout primitives.
 *
 * The print stylesheet hooks onto it — `day` starts a page, `hide` removes
 * navigation, `stamp` appears only on paper. Threading it through here rather
 * than wrapping every printable block in an extra div keeps the markup honest.
 */
type PrintRole = 'day' | 'hide' | 'act' | 'stamp' | 'sheet' | 'stage';

interface LayoutProps {
  gap?: number; children: ReactNode; style?: CSSProperties; 'data-print'?: PrintRole;
  /*
   * A layout primitive still has to be able to carry meaning. Without these,
   * an alert region or a list had to be wrapped in a bare div beside the
   * Stack, which put the semantics on a different element from the layout.
   */
  role?: string; 'aria-label'?: string; 'aria-live'?: 'polite' | 'assertive'; 'aria-busy'?: boolean;
  /** A class from `components.css` / `tokens.css`, alongside the layout. */
  className?: string;
}

/*
 * The layout itself is a class (`ui-stack` / `ui-row`, zero specificity in
 * components.css) and only the gap — a prop — is inline. When `display` and
 * `align-items` were inline, a screen's class could never override them.
 */
export function Stack({ gap = 12, children, style, className, ...rest }: LayoutProps) {
  return <div {...rest} className={cx('ui-stack', className)} style={{ gap, ...style }}>{children}</div>;
}

export function Row({ gap = 9, children, style, className, ...rest }: LayoutProps) {
  return <div {...rest} className={cx('ui-row', className)} style={{ gap, ...style }}>{children}</div>;
}

export function Spinner({ label }: { label: string }) {
  return <div role="status" className="ui-spinner">{label}</div>;
}

/**
 * What the app says while it is waiting for everything.
 *
 * "One moment…" in 13px grey is a browser's apology. This is the one screen
 * every single visit passes through — the boot refresh, the tenant lookup,
 * the lineup download — and on festival wifi it is on screen for seconds, so
 * it is worth being something.
 *
 * The mark, big and alone, then the name, then a line from the field. The
 * line is the only part that is not chrome: somebody standing in a queue at
 * 03:00 reading "waiting for the drop" knows the app is on their side in a
 * way that "Loading…" cannot say.
 *
 * The lines are written out inside the component, each through `tr()`, so
 * every one of them has its Hebrew — a list kept at module level is a list
 * the translation check cannot see.
 */
function useLoadingLines(): string[] {
  const tr = useUiT();
  return [
    tr('Waiting for the drop…'),
    tr('Queuing at the gate…'),
    tr('Tuning the sound system…'),
    tr('Finding the good speaker…'),
    tr('Counting down to sunrise…'),
    tr('Saving your spot at the rail…'),
    tr('Chasing the bassline…'),
    tr('Charging the power bank…'),
    tr('Asking around for set times…'),
    tr('Unfolding the map…'),
    tr('Rolling in from the car park…'),
    tr('Warming up the decks…'),
  ];
}

/**
 * The full-page wait.
 *
 * `label` is what a screen reader is told and what a test can look for — the
 * plain truth, "Loading the lineup". The line underneath is decoration and is
 * hidden from the accessibility tree, because "chasing the bassline" is not
 * an answer to "what is happening".
 *
 * The line is picked ONCE per mount, in the state initialiser. Picking it
 * during render would deal a new one on every re-render — and this screen
 * re-renders whenever anything above it does, so the text would flicker
 * through the whole list while somebody tried to read it.
 */
export function LoadingScreen({ label, brand, detail }: {
  label: string;
  /**
   * In place of the MyRave wordmark: a rave's own logo while ITS lineup
   * loads (the user, 2026-10-07). The mark stays above it, spinning.
   */
  brand?: ReactNode;
  /** One line under the brand — the rave's dates and place. */
  detail?: string;
}) {
  const lines = useLoadingLines();
  /*
   * The spinner's angle comes from the CLOCK, not from when this screen
   * mounted (the user, 2026-10-07): one loading screen giving way to the next
   * — or a redirect to another page — carries on where the last one was,
   * exactly as the launch screen in index.html does. Same lap, same angle.
   */
  const [phase] = useState(() => ({
    '--spin-phase': `${-(Date.now() % SPIN_LAP_MS)}ms`,
  }) as CSSProperties);

  return (
    /* `data-boot-wait`: the launch screen in index.html stays up while one of these is on the page. */
    /*
     * The whole screen, centred on it — the launch screen's own geometry
     * (index.html `#splash`): the same mark at the same place, the words
     * under it. So the launch screen giving way to this, or this to the next
     * one, moves nothing (the user, 2026-10-07).
     */
    <div role="status" aria-label={label} aria-busy="true" className="ui-loading" data-boot-wait="" style={phase}>
      <span aria-hidden="true" className="loading-mark">
        {/* The same size with or without a rave: only the words below it change. */}
        <SpinnerMark />
      </span>
      <div className="ui-loading-below">
        <span aria-hidden="true" className="ui-inline">
          {brand ?? <Wordmark size={34} />}
        </span>
        {detail && <span aria-hidden="true" className="ui-loading-detail">{detail}</span>}
        <TypedLine fallback={lines} />
      </div>
    </div>
  );
}

/**
 * The loading spinner: the MyRave mark in LAYERS — a still drawing, the orbit
 * (a whole `<svg>`, turned) and the five bars as boxes scaled from their feet
 * — so a phone's compositor moves it. The same mark animated inside one SVG
 * stuttered on an iPhone (the user, 2026-10-07): Safari redrew the drawing
 * every frame. Geometry is the mark's 160-unit viewBox in per cent; the
 * launch screen in index.html is built the same way.
 */
export function SpinnerMark() {
  return (
    <span className="ui-spinner-mark">
      <svg viewBox="0 0 160 160" fill="none">
        <circle cx="80" cy="80" r="44" className="m-ring" strokeWidth="1" />
        <circle cx="80" cy="80" r="32" className="ui-spinner-disc" />
      </svg>
      <svg viewBox="0 0 160 160" fill="none" className="ui-spinner-orbit">
        <circle cx="80" cy="80" r="54" className="ui-spinner-ring" strokeWidth="1.2" strokeDasharray="5 7" />
        <circle cx="97.25" cy="29" r="4" className="ui-spinner-beacon" />
        <circle cx="97.25" cy="29" r="7.5" className="ui-spinner-halo" strokeWidth="1.4" />
      </svg>
      <span className="ui-spinner-bars"><i /><i /><i /><i /><i /></span>
    </span>
  );
}

/** One lap of the turning orbit — the launch screen's `TURN` in index.html. */
export const SPIN_LAP_MS = 2400;

/**
 * An empty state that says what would fill it.
 *
 * "No results" tells someone nothing. What they can do next is the useful part.
 */
export function Empty({
  title, hint, action, framed,
}: {
  title: string; hint?: string; action?: ReactNode;
  /**
   * In a card, where the empty state stands in for a LIST that would have
   * been there — the schedule's "nothing on this day". The card is the frame
   * the stage sections would have had, so the page keeps its shape.
   */
  framed?: boolean;
}) {
  const body = (
    <Stack gap={10} className="ui-empty">
      <div className="ui-empty-title">{title}</div>
      {hint && <div className="ui-empty-hint">{hint}</div>}
      {action}
    </Stack>
  );
  return framed ? <Card>{body}</Card> : body;
}

/** Escape closes. Shared by both dialogs. */
function useEscape(onEscape: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onEscape(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onEscape]);
}

/**
 * "Are you sure?", for the handful of actions where the answer might be no.
 *
 * **Used only where the action is hard to take back.** A confirm on something
 * reversible is a click everybody learns to dismiss without reading, which is
 * exactly the habit that makes the one that matters useless.
 *
 * Three things it does that an inline `confirm()` cannot, and each is the
 * reason this exists rather than the browser's:
 *
 *  - **It says what will happen AND what will not.** Most of the fear in a
 *    destructive action is about the thing it does not touch. Stopping
 *    attending a rave keeps every act you saved, and a dialog that does not
 *    say so is a dialog that gets cancelled.
 *  - **Cancel takes the focus**, not the confirm. Enter on a dialog somebody
 *    has not read should do nothing.
 *  - **Escape and the scrim both cancel**, because a way out that is only a
 *    button is a way out somebody on a phone has to aim for.
 */
export function ConfirmDialog({
  title, body, confirmLabel, cancelLabel,
  tone = 'primary', busy = false, onConfirm, onCancel, icon, className,
}: {
  title: string;
  body?: ReactNode;
  /** Drawn before the title (a screen's design — Sign-in security's bin). */
  icon?: ReactNode;
  /** On the scrim, so a screen's stylesheet can draw the dialog to its design. */
  className?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const tr = useUiT();
  useEscape(onCancel);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      className={cx('ui-scrim is-alert', className)}
    >
      <div role="alertdialog" aria-modal="true" aria-label={title} className="ui-dialog is-narrow">
        <Stack gap={12}>
          {icon
            ? <div className="ui-dialog-head">{icon}<div className="ui-dialog-title">{title}</div></div>
            : <div className="ui-dialog-title">{title}</div>}
          {body && <div className="ui-dialog-body">{body}</div>}
          {/*
            * Cancel first in the DOM so it takes the focus and so a screen
            * reader reaches the way out before the way through.
            */}
          <Row gap={8} className="ui-actions">
            <Button autoFocus variant="secondary" onClick={onCancel} disabled={busy}>
              {cancelLabel ?? tr('Cancel')}
            </Button>
            <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy}>
              {busy ? tr('One moment…') : (confirmLabel ?? tr('Confirm'))}
            </Button>
          </Row>
        </Stack>
      </div>
    </div>
  );
}

/**
 * An error state that says what to do, not what failed.
 *
 * There was no error component at all. Every screen hand-rolled
 * `<div style={{ fontSize: 12, color: 'var(--bad)' }}>` and they had drifted to
 * five different sizes — 10.5, 11.5, 12, 12.5 and 13 — so the same failure
 * looked like five different severities depending on where you hit it.
 *
 * `retry` is offered but never required: a festival is a field with no
 * reception, and the honest thing to show is usually the stored copy plus a
 * way to try again, not a dead end.
 */
export function ErrorState({
  title, detail, retry, retryLabel, tip, retryVariant = 'secondary',
}: {
  title: string; detail?: string; retry?: () => void; retryLabel?: string;
  /**
   * One practical thing to do about it, set apart from the explanation — the
   * Stitch "no network, no stored copy" state's field tip. The detail says
   * what happened; the tip says what to do.
   */
  tip?: ReactNode;
  /** `primary` where trying again is the ONLY way forward. */
  retryVariant?: 'primary' | 'secondary';
}) {
  const tr = useUiT();
  return (
    <div role="alert" className="ui-screen">
      {/*
        * The brand first, not a red warning circle.
        *
        * Same face as the loading screen, because to the person holding the
        * phone this IS the loading screen — it is the same wait, with worse
        * news at the end of it. A red badge makes a lost connection look like
        * something they broke; the words are where the problem belongs, and
        * they are written to be read at 04:00 by somebody who does not care
        * whose fault it is.
        *
        * Not spinning: the difference between "still going" and "stopped" is
        * the whole message.
        */}
      <span aria-hidden="true" className="ui-inline">
        <LogoMark size={112} />
      </span>

      <div className="ui-screen-title">{title}</div>
      {detail && <div className="ui-screen-detail">{detail}</div>}
      {tip && <div className="ui-screen-tip">{tip}</div>}
      {retry && (
        <span className="ui-screen-retry">
          <Button variant={retryVariant} onClick={retry}>{retryLabel ?? tr('Try again')}</Button>
        </span>
      )}
    </div>
  );
}

/**
 * A loading placeholder shaped like the thing that is coming.
 *
 * A spinner in the middle of a schedule tells someone the app is busy; a
 * skeleton tells them what is about to be there, and the difference matters
 * most on the connection where the wait is longest. `Spinner` stays for the
 * cases with no predictable shape.
 *
 * The shimmer is a background-position animation, so it is one paint and it
 * stops entirely under `prefers-reduced-motion` via the global rule in
 * tokens.css.
 */
export function Skeleton({
  width = '100%', height = 14, radius = 'var(--r-sm)', style, className,
}: { width?: number | string; height?: number | string; radius?: string; style?: CSSProperties; className?: string }) {
  return <span aria-hidden="true" className={cx('ui-skel', className)} style={{ width, height, borderRadius: radius, ...style }} />;
}

/** One act row's worth of skeleton, at the real row's proportions. */
export function ActRowSkeleton() {
  return (
    <Row gap={10} className="ui-skel-act">
      <Stack gap={4} className="ui-skel-times">
        <Skeleton width={34} height={11} />
        <Skeleton width={26} height={9} />
      </Stack>
      <Stack gap={5} className="ui-grow">
        <Skeleton width="62%" height={13} />
        <Skeleton width="38%" height={10} />
      </Stack>
      <Skeleton width={34} height={34} radius="var(--r-pill)" />
    </Row>
  );
}

/**
 * One stage section's worth of skeleton: the header strip and a few rows.
 *
 * The same frame as the real section — radius, border, card ground — so the
 * page does not change shape when the lineup lands, only fills in.
 */
export function StageSectionSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="ui-stage">
      <Row gap={10} className="ui-skel-stage-hd">
        <Row gap={10} className="ui-grow">
          <Skeleton width={10} height={10} radius="var(--r-pill)" />
          <Skeleton width="42%" height={17} />
        </Row>
        <Skeleton width={52} height={22} radius="var(--r-pill)" />
      </Row>
      {Array.from({ length: rows }, (_, i) => <ActRowSkeleton key={i} />)}
    </div>
  );
}


/**
 * The schedule while its lineup is on the way — track tabs, day pills and
 * stage sections, in the places they will be (the Stitch loading state).
 *
 * `label` is what a screen reader is told and what a test looks for; every
 * shape inside is `aria-hidden`, because "grey bar, grey bar" is not news.
 * The radii are the real controls' own: 12px is `TrackTab`, 13px `DayPill`.
 */
export function ScheduleSkeleton({ label, sections = 2 }: { label: string; sections?: number }) {
  return (
    <div role="status" aria-label={label} aria-busy="true" className="ui-skel-schedule" data-boot-wait="">
      <Row gap={6} className="ui-skel-tabs">
        {[0, 1, 2].map((i) => <Skeleton key={i} height={44} radius="12px" className="ui-grow" />)}
      </Row>
      <Row gap={7} className="ui-skel-days">
        {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} height={44} radius="13px" className="ui-skel-day" />)}
      </Row>
      <Stack gap={14} className="ui-skel-sections">
        {Array.from({ length: sections }, (_, i) => <StageSectionSkeleton key={i} rows={i === 0 ? 3 : 2} />)}
      </Stack>
    </div>
  );
}

/**
 * A notice that sits INLINE above content and never over it: an icon, what
 * is true, since when, and an optional badge at the end.
 *
 * Neutral by default. The use today is "you are reading the copy stored on
 * this phone", and at a festival that is the product working, not failing —
 * a warning colour would say the opposite.
 */
export function Notice({
  title, detail, icon, trailing, tone = 'neutral',
}: {
  title: string; detail?: string; icon?: ReactNode; trailing?: ReactNode;
  tone?: 'neutral' | 'warn';
}) {
  return (
    <div role="status" className={cx('ui-notice', tone === 'warn' && 'is-warn')}>
      {icon && <span aria-hidden="true" className="ui-notice-icon">{icon}</span>}
      <div className="ui-notice-text">
        <div className="ui-notice-title">{title}</div>
        {detail && <div className="ui-notice-detail">{detail}</div>}
      </div>
      {trailing}
    </div>
  );
}

/* ------------------------------------------------------------ forms ----- */

/**
 * A labelled form control — the label, the control, a hint, and the error AT
 * the field.
 *
 * A field-level refusal from the API (`details: [{ field, message }]`) is only
 * useful if it lands beside the input it names. `children` is a render
 * function handed the id, so the `<label for>` pairing cannot be forgotten and
 * the error is announced with the control (`aria-describedby`).
 */
export function FormField({
  label, hint, error, warn, children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** A consequence that is not an error — "changing this moves every act". */
  warn?: string;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
}) {
  const id = useId();
  const described = [hint ? `${id}-hint` : '', warn ? `${id}-warn` : '', error ? `${id}-error` : '']
    .filter(Boolean).join(' ') || undefined;
  return (
    <Stack gap={5}>
      <label htmlFor={id} className="ui-label">{label}</label>
      {children({ id, 'aria-describedby': described, 'aria-invalid': error ? true : undefined })}
      {hint && <span id={`${id}-hint`} className="ui-hint">{hint}</span>}
      {warn && <span id={`${id}-warn`} className="ui-warn">⚠ {warn}</span>}
      {error && <span id={`${id}-error`} role="alert" className="ui-err">{error}</span>}
    </Stack>
  );
}

/**
 * A modal for a FORM — an act, a stage, an import.
 *
 * Unlike `ConfirmDialog` it is a `dialog`, not an `alertdialog`: it asks for
 * input rather than a yes or no. Escape and the scrim close it, as there.
 */
export function Dialog({
  title, onClose, children, footer, wide, centered,
}: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode;
  /** For a table — the import's per-row report. */
  wide?: boolean;
  /** In the middle of a phone's screen too, never the bottom sheet (What's new — the user, 2026-10-27). */
  centered?: boolean;
}) {
  const tr = useUiT();
  useEscape(onClose);
  // The page holds still under it — on a phone it scrolled, and pulled the
  // eye (and the pull-to-refresh) along with it.
  useEffect(() => lockScroll(), []);

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} className={cx('ui-scrim', centered && 'is-centered')}>
      <div role="dialog" aria-modal="true" aria-label={title} className={cx('ui-dialog', wide && 'is-wide')}>
        <Stack gap={14}>
          <Row gap={8} className="ui-between">
            <div className="ui-dialog-title">{title}</div>
            <button onClick={onClose} aria-label={tr('Close')} className="ui-dialog-close">✕</button>
          </Row>
          {children}
          {footer && <Row gap={8} className="ui-actions">{footer}</Row>}
        </Stack>
      </div>
    </div>
  );
}

/**
 * A `#hex` colour — a text box and a swatch of what it says.
 *
 * Text rather than `<input type="color">` alone: a festival's palette arrives
 * as hex from a brand sheet and is pasted, and the native picker cannot show
 * an invalid value — which is exactly the one the operator needs to see.
 */
export function ColorInput({
  value, onChange, id, ...aria
}: {
  value: string; onChange: (v: string) => void; id?: string;
  'aria-describedby'?: string; 'aria-invalid'?: boolean;
}) {
  const valid = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
  return (
    <Row gap={8}>
      <span
        aria-hidden="true"
        data-testid="swatch"
        className="ui-swatch"
        style={{ background: valid ? value : 'transparent' }}
      />
      <input
        id={id}
        {...aria}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#rrggbb"
        spellCheck={false}
        dir="ltr"
        className="ui-input is-mono"
      />
    </Row>
  );
}

/** Country code to flag emoji — the pilot's trick, and it is a good one. */
export function flag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return '';
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}
export * from './dates';
export { mix, Txt, SrOnly, srOnly, type Tone } from './txt';
export { Sheet, focusables } from './Sheet';
export { Tabs, isRtl, type TabItem } from './Tabs';
export { ToastProvider, useToast, TOAST_DURATION, type ToastApi, type ToastOptions, type ToastTone } from './Toast';
